import { useMemo, useState } from 'react'
import type { TestRun } from '@sophia/shared-schemas'
import { ArrowRight, GitCompare, Info, Minus, TrendingDown, TrendingUp } from 'lucide-react'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { Badge } from '@/components/ui/Badge'
import { Tooltip } from '@/components/ui/Tooltip'
import { cn } from '@/lib/utils'
import { compareRuns, type MetricDelta } from '../lib/run-comparison'

interface Props {
  runs: TestRun[]
}

function runLabel(run: TestRun): string {
  return `${new Date(run.startedAt).toLocaleString('pt-BR')} · ${run.status}`
}

function fmtValue(value: number, format: MetricDelta['format']): string {
  if (format === 'pct') return `${(value * 100).toFixed(1)}%`
  return String(value)
}

function fmtDelta(d: MetricDelta): string {
  if (d.delta === 0) return '0'
  const sign = d.delta > 0 ? '+' : '−'
  if (d.format === 'pct') {
    const pp = Math.abs(d.delta * 100).toFixed(1)
    return `${sign}${pp} p.p.`
  }
  return `${sign}${Math.abs(d.delta)}`
}

function DeltaRow({ d }: { d: MetricDelta }) {
  const tone =
    d.improved === null ? 'neutral' : d.improved ? 'success' : 'danger'
  const toneText =
    tone === 'success' ? 'text-success' : tone === 'danger' ? 'text-danger' : 'text-fg-3'
  const Icon = d.improved === null ? Minus : d.improved ? TrendingUp : TrendingDown
  return (
    <tr className="border-b border-border-subtle last:border-0">
      <td className="px-4 py-3 text-sm text-fg-1">{d.label}</td>
      <td className="px-4 py-3 text-right data-mono text-sm text-fg-2">{fmtValue(d.aValue, d.format)}</td>
      <td className="px-2 py-3 text-center">
        <ArrowRight className="h-3.5 w-3.5 text-fg-3 inline" />
      </td>
      <td className="px-4 py-3 text-right data-mono text-sm font-semibold text-fg-1">
        {fmtValue(d.bValue, d.format)}
      </td>
      <td className="px-4 py-3 text-right">
        <span className={cn('inline-flex items-center gap-1 data-mono text-sm font-semibold', toneText)}>
          <Icon className="h-3.5 w-3.5" />
          {fmtDelta(d)}
        </span>
      </td>
    </tr>
  )
}

export function ComparisonTab({ runs }: Props) {
  // só runs com métricas dá pra comparar
  const comparable = useMemo(
    () => runs.filter((r) => r.status === 'COMPLETED' && r.metrics),
    [runs],
  )

  // default: A = run anterior (mais antiga das duas recentes), B = mais recente
  const [aId, setAId] = useState<string>(() => comparable[1]?.id ?? '')
  const [bId, setBId] = useState<string>(() => comparable[0]?.id ?? '')

  if (comparable.length < 2) {
    return (
      <EmptyState
        icon={GitCompare}
        title="Precisa de pelo menos 2 runs concluídas"
        description='A comparação mostra o antes/depois entre duas execuções da mesma base. Rode a base novamente (botão "Rodar novamente" lá em cima) depois de ajustar as regras — aí você compara o resultado das duas versões aqui.'
      />
    )
  }

  const runA = comparable.find((r) => r.id === aId) ?? comparable[1]
  const runB = comparable.find((r) => r.id === bId) ?? comparable[0]
  const sameRun = runA.id === runB.id
  const comparison = sameRun ? null : compareRuns(runA, runB)

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-end">
            <label className="flex flex-col gap-1">
              <span className="text-2xs uppercase tracking-wider text-fg-3">Run A (referência)</span>
              <select
                value={runA.id}
                onChange={(e) => setAId(e.target.value)}
                className="h-10 px-3 rounded-md border border-border bg-bg-surface text-sm text-fg-1"
              >
                {comparable.map((r) => (
                  <option key={r.id} value={r.id}>
                    {runLabel(r)}
                  </option>
                ))}
              </select>
            </label>
            <div className="hidden md:flex items-center justify-center pb-2.5">
              <ArrowRight className="h-4 w-4 text-fg-3" />
            </div>
            <label className="flex flex-col gap-1">
              <span className="text-2xs uppercase tracking-wider text-fg-3">Run B (comparada)</span>
              <select
                value={runB.id}
                onChange={(e) => setBId(e.target.value)}
                className="h-10 px-3 rounded-md border border-border bg-bg-surface text-sm text-fg-1"
              >
                {comparable.map((r) => (
                  <option key={r.id} value={r.id}>
                    {runLabel(r)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </CardBody>
      </Card>

      {sameRun ? (
        <p className="text-sm text-fg-3 italic px-1">Selecione duas runs diferentes pra ver as diferenças.</p>
      ) : comparison ? (
        <>
          <div className="rounded-md border border-brand-100 bg-brand-50 px-4 py-3 text-sm text-fg-1">
            <span className="font-semibold text-brand-700">Resumo:</span> {comparison.summary}
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-1.5">
                <h3 className="font-display font-bold text-base text-fg-1">Métricas principais</h3>
                <Tooltip
                  align="start"
                  content="Falsos positivos: a regra previu uma classe e errou. Falsos negativos: a linha era de uma classe cadastrada e não foi identificada corretamente (errou a classe ou não classificou)."
                >
                  <Info className="h-3.5 w-3.5 text-fg-3/60 hover:text-fg-2 transition-colors cursor-help" />
                </Tooltip>
              </div>
              <p className="text-sm text-fg-3 mt-0.5">
                Ganhos em verde, perdas em vermelho. Delta em pontos percentuais (p.p.) pra taxas.
              </p>
            </CardHeader>
            <CardBody className="p-0 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-2xs uppercase tracking-wider text-fg-3 border-b border-border-subtle">
                    <th className="px-4 py-2 text-left font-semibold">Métrica</th>
                    <th className="px-4 py-2 text-right font-semibold">Run A</th>
                    <th className="px-2"></th>
                    <th className="px-4 py-2 text-right font-semibold">Run B</th>
                    <th className="px-4 py-2 text-right font-semibold">Delta</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.rows.map((d) => (
                    <DeltaRow key={d.key} d={d} />
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card>
              <CardHeader>
                <h3 className="font-display font-bold text-base text-fg-1">Classes impactadas</h3>
              </CardHeader>
              <CardBody>
                <div className="data-mono text-3xl font-bold text-fg-1">{comparison.classesImpacted}</div>
                <p className="text-xs text-fg-3 mt-1">
                  classes com contagens diferentes entre as duas runs.
                </p>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="font-display font-bold text-base text-fg-1">
                  Regras problemáticas{' '}
                  <span className="text-fg-3 font-normal data-mono text-sm">
                    ({comparison.problematicRules.length})
                  </span>
                </h3>
                <p className="text-sm text-fg-3 mt-0.5">Precisão caiu ou falsos positivos subiram na Run B.</p>
              </CardHeader>
              <CardBody>
                {comparison.problematicRules.length === 0 ? (
                  <p className="text-sm text-fg-3 italic">Nenhuma regra piorou. 🎯</p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {comparison.problematicRules.slice(0, 6).map((r) => (
                      <li key={r.ruleId} className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm text-fg-1 truncate">{r.ruleName}</div>
                          <div className="text-2xs text-fg-3 data-mono">{r.className}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {r.fpA !== null && (
                            <span className="text-2xs text-fg-3 data-mono">FP {r.fpA}→{r.fpB}</span>
                          )}
                          <Badge tone="danger">
                            {r.precisionA !== null
                              ? `${(r.precisionA * 100).toFixed(0)}%→${(r.precisionB * 100).toFixed(0)}%`
                              : 'nova regra'}
                          </Badge>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>
          </div>
        </>
      ) : (
        <p className="text-sm text-danger px-1">Uma das runs selecionadas não tem métricas.</p>
      )}
    </div>
  )
}
